import { PlusInSquareIcon } from "assets/icons";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean } from "hooks";
import { BoxShadowLayout } from "layouts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ItemsTable, Pagination, SearchInput } from "ui";

import { InternalBankHistoryItemEntity } from "../../constants";
import { useInternalBankHistoryList } from "../../hooks";
import { ExpenseModal } from "../modals";
import { InternalBankHistoryTableProps } from "./InternalBankHistoryTable.types";
import { useInternalBankHistoryTableColumns } from "./useInternalBankHistoryTableColumns";

import styles from "./InternalBankHistoryTable.module.scss";

const InternalBankHistoryTable = ({
  dateRange
}: InternalBankHistoryTableProps) => {
  const { t } = useTranslation();

  const {
    history,
    totalItems,
    searchValue,
    sorting,
    setSearchValue,
    setSorting,
    page,
    setPage,
    isLoading
  } = useInternalBankHistoryList(dateRange);

  const {
    value: isOpenExpenseModal,
    setTrue: onOpenExpenseModal,
    setFalse: onCloseExpenseModal
  } = useBoolean();

  const {
    columns,
    proofOfPaymentImagesModal,
    proofOfExpenseImagesModal,
    editModal
  } = useInternalBankHistoryTableColumns();

  const replenishRowsIndexes = useMemo(
    () =>
      history
        .map((item, index) =>
          item.entity === InternalBankHistoryItemEntity.InternalBankHistory
            ? index
            : undefined
        )
        .filter((index) => index !== undefined),
    [history]
  );

  return (
    <BoxShadowLayout className={styles.expenses}>
      <div className={styles.expenses_header}>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t("Search...")}
        />

        <Button icon={<PlusInSquareIcon />} onClick={onOpenExpenseModal}>
          {t("Add New Expense")}
        </Button>
      </div>

      <ItemsTable
        columns={columns}
        data={history}
        sorting={sorting}
        skeletonHeight={65}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.expenses_table}
        specificRowsIndexes={replenishRowsIndexes as number[]}
        specificRowsClassName={styles.expenses_table_replenishRow}
        rowsClassName={styles.expenses_table_tableRow}
        headerRowClassName={styles.expenses_table_tableHeaderRow}
      />

      <Pagination
        currentPage={page}
        onChange={setPage}
        perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
        totalItems={totalItems}
      />

      {isOpenExpenseModal && (
        <ExpenseModal
          isOpen={isOpenExpenseModal}
          onClose={onCloseExpenseModal}
        />
      )}

      {proofOfPaymentImagesModal}
      {proofOfExpenseImagesModal}
      {editModal}
    </BoxShadowLayout>
  );
};

export default InternalBankHistoryTable;
